import { useState, type FormEvent } from "react";
import { Link, useLocation, useSearchParams } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Input, Stack } from "@wato/ui";
import { useApi, useErrorMessage } from "../../api/context";
import { AuthLayout } from "../../components/common";

/** `?error=` codes from `GET /v1/auth/verify-email` (ADR-0012) → the API's own messages. */
const ERROR_MESSAGES: Readonly<Record<string, MessageKey>> = {
  AUTH_TOKEN_INVALID: "auth.errors.tokenInvalid",
  AUTH_TOKEN_USED: "auth.errors.tokenUsed",
  AUTH_TOKEN_EXPIRED: "auth.errors.tokenExpired",
  AUTH_ACCOUNT_DISABLED: "auth.errors.accountDisabled",
};

/**
 * "Check your email" after sign-up, and the landing page for a failed verification link.
 * The address is kept in router state only (never in the URL).
 */
export function VerifyEmailPage() {
  const { t } = useI18n();
  const api = useApi();
  const errorMessage = useErrorMessage();
  const [params] = useSearchParams();
  const location = useLocation();
  const state = (location.state ?? {}) as { email?: string };
  const [email, setEmail] = useState(state.email ?? "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const code = params.get("error");
  const failed = code !== null;

  async function resend(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.resendVerification(email.trim());
      setDone(true);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t(failed ? "dashboard.auth.verify.errorTitle" : "dashboard.auth.verify.title")}
      footer={
        <Link className="font-semibold text-on-action-ghost underline" to="/login">
          {t("dashboard.auth.backToLogin")}
        </Link>
      }
    >
      <Stack gap={4}>
        {failed ? (
          <Alert tone="error">{t(ERROR_MESSAGES[code] ?? "auth.errors.tokenInvalid")}</Alert>
        ) : (
          <Alert tone="info">{t("dashboard.auth.verify.sent")}</Alert>
        )}
        <form onSubmit={resend} noValidate>
          <Stack gap={3}>
            <h2 className="text-base font-semibold text-fg">
              {t("dashboard.auth.verify.resendTitle")}
            </h2>
            {done && <Alert tone="success">{t("dashboard.auth.verify.resendDone")}</Alert>}
            {error && <Alert tone="error">{error}</Alert>}
            <Input
              type="email"
              dir="ltr"
              autoComplete="email"
              required
              label={t("dashboard.auth.email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" variant="secondary" loading={busy}>
              {t("dashboard.auth.verify.resendSubmit")}
            </Button>
          </Stack>
        </form>
      </Stack>
    </AuthLayout>
  );
}
